import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Chip, LoadingState, StatCard, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, MagnifyingGlass, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { VEHICLE_TYPES, VEHICLE_TYPE_MAP, FUEL_TYPES, VEHICLE_STATUSES, VEHICLE_STATUS_LABELS, VEHICLE_STATUS_VARIANTS, APPROVAL_STATUS_LABELS, APPROVAL_STATUS_VARIANTS } from "@/lib/parkingTypes";
import VehicleFormDialog from "@/components/vehicles/VehicleFormDialog";
import VehicleProfileDialog from "@/components/vehicles/VehicleProfileDialog";

// Vehicle Management V2 — the resident-owned Vehicle lifecycle registry (Phase 1: core
// foundation). Built entirely on the existing `parking_vehicles` collection (Parking V2's own
// vehicle registry, extended additively) — a separate domain from Parking (slots/allocation/
// billing/passes, untouched) and from Gate/Security (entry-exit/approval, untouched — see
// Gate -> "Vehicle Approval").
export default function VehicleManagement() {
    const [vehicles, setVehicles] = useState([]);
    const [flats, setFlats] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [filterFlat, setFilterFlat] = useState("all");
    const [filterVehicleType, setFilterVehicleType] = useState("all");
    const [filterParking, setFilterParking] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterFuel, setFilterFuel] = useState("all");
    const [filterApproval, setFilterApproval] = useState("all");

    const [formOpen, setFormOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [profileVehicle, setProfileVehicle] = useState(null);
    const [rejectVehicle, setRejectVehicle] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [approving, setApproving] = useState(false);

    async function loadVehicles() {
        try {
            const { data } = await api.get("/parking/vehicles");
            setVehicles(data || []);
        } catch (e) { toast.error(formatError(e)); }
    }
    async function loadFlats() {
        try {
            const { data } = await api.get("/flats");
            setFlats(data || []);
        } catch (e) { toast.error(formatError(e)); }
    }

    useEffect(() => {
        (async () => { setLoading(true); await Promise.all([loadVehicles(), loadFlats()]); setLoading(false); })();
    }, []);

    function openAdd() { setEditingVehicle(null); setFormOpen(true); }
    function openEdit(v) { setEditingVehicle(v); setFormOpen(true); }

    async function approveVehicle(v) {
        setApproving(true);
        try {
            await api.post(`/parking-vehicles/${v.id}/approve`);
            toast.success(`${v.registration_number} approved`);
            loadVehicles();
            if (profileVehicle?.id === v.id) setProfileVehicle(null);
        } catch (e) { toast.error(formatError(e)); } finally { setApproving(false); }
    }
    function openReject(v) { setRejectVehicle(v); setRejectReason(""); }
    async function submitReject(e) {
        e.preventDefault();
        if (!rejectReason.trim()) { toast.error("Please provide a reason for rejection"); return; }
        setApproving(true);
        try {
            await api.post(`/parking-vehicles/${rejectVehicle.id}/reject`, { rejection_reason: rejectReason.trim() });
            toast.success(`${rejectVehicle.registration_number} rejected`);
            setRejectVehicle(null);
            loadVehicles();
            if (profileVehicle?.id === rejectVehicle.id) setProfileVehicle(null);
        } catch (e) { toast.error(formatError(e)); } finally { setApproving(false); }
    }

    const stats = useMemo(() => {
        const active = vehicles.filter((v) => (v.status || "active") === "active").length;
        const withParking = vehicles.filter((v) => v.parking_slot_number).length;
        const pendingApproval = vehicles.filter((v) => (v.approval_status || "approved") === "pending").length;
        return { total: vehicles.length, active, withParking, withoutParking: vehicles.length - withParking, pendingApproval };
    }, [vehicles]);

    const filtered = useMemo(() => {
        let rows = vehicles;
        if (filterFlat !== "all") rows = rows.filter((v) => v.flat_id === filterFlat);
        if (filterVehicleType !== "all") rows = rows.filter((v) => v.vehicle_type === filterVehicleType);
        if (filterParking === "with") rows = rows.filter((v) => v.parking_slot_number);
        if (filterParking === "without") rows = rows.filter((v) => !v.parking_slot_number);
        if (filterStatus !== "all") rows = rows.filter((v) => (v.status || "active") === filterStatus);
        if (filterFuel !== "all") rows = rows.filter((v) => v.fuel_type === filterFuel);
        if (filterApproval !== "all") rows = rows.filter((v) => (v.approval_status || "approved") === filterApproval);
        const term = search.trim().toLowerCase();
        if (term) {
            rows = rows.filter((v) =>
                (v.registration_number || "").toLowerCase().includes(term) ||
                (v.resident_name || "").toLowerCase().includes(term) ||
                (v.flat_label || "").toLowerCase().includes(term)
            );
        }
        return rows;
    }, [vehicles, filterFlat, filterVehicleType, filterParking, filterStatus, filterFuel, filterApproval, search]);

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up">
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-brand-inkSoft">Resident-owned vehicle registry — ownership, details and lifecycle, linked to Parking where applicable.</p>
                <Button data-testid="add-vehicle-mgmt-btn" onClick={openAdd} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white shrink-0">
                    <Plus size={16} className="mr-2" /> Add vehicle
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <StatCard testId="stat-total-vehicles" label="Total vehicles" value={stats.total} />
                <StatCard testId="stat-active-vehicles" label="Active" value={stats.active} />
                <StatCard testId="stat-pending-approval" label="Pending approval" value={stats.pendingApproval} />
                <StatCard testId="stat-with-parking" label="With parking" value={stats.withParking} />
                <StatCard testId="stat-without-parking" label="Without parking" value={stats.withoutParking} />
            </div>

            <div className="bg-white border border-brand-line rounded-sm p-4 mb-6 space-y-3">
                <div className="flex items-center gap-3">
                    <MagnifyingGlass size={18} className="text-brand-inkSoft shrink-0" />
                    <Input data-testid="vehicle-mgmt-search-input" placeholder="Search registration number, resident or flat…" value={search}
                        onChange={(e) => setSearch(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-0" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <Select value={filterFlat} onValueChange={setFilterFlat}>
                        <SelectTrigger data-testid="filter-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Flat" /></SelectTrigger>
                        <SelectContent className="max-h-64">
                            <SelectItem value="all">All flats</SelectItem>
                            {flats.map((f) => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filterVehicleType} onValueChange={setFilterVehicleType}>
                        <SelectTrigger data-testid="filter-vehicle-type-mgmt-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Vehicle type" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All types</SelectItem>{VEHICLE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={filterApproval} onValueChange={setFilterApproval}>
                        <SelectTrigger data-testid="filter-approval-status-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Approval" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All approval states</SelectItem>
                            <SelectItem value="pending">Pending Approval</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filterParking} onValueChange={setFilterParking}>
                        <SelectTrigger data-testid="filter-parking-status-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Parking" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">With & without parking</SelectItem>
                            <SelectItem value="with">With parking</SelectItem>
                            <SelectItem value="without">Without parking</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger data-testid="filter-vehicle-status-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All statuses</SelectItem>{VEHICLE_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={filterFuel} onValueChange={setFilterFuel}>
                        <SelectTrigger data-testid="filter-fuel-type-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Fuel type" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All fuel types</SelectItem>{FUEL_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </div>

            {filtered.length === 0 ? (
                <EmptyState title="No vehicles found" description={vehicles.length === 0 ? "Add the first vehicle, or residents can register their own via My Flat." : "Try adjusting your search or filters."} />
            ) : (
                <div className="bg-white border border-brand-line rounded-sm overflow-x-auto">
                    <table className="w-full text-sm min-w-[1060px]" data-testid="vehicle-mgmt-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-overline text-brand-inkSoft border-b border-brand-line bg-brand-bg">
                                <th className="py-3 px-4">Vehicle Number</th>
                                <th className="py-3 px-4">Type</th>
                                <th className="py-3 px-4">Resident</th>
                                <th className="py-3 px-4">Flat</th>
                                <th className="py-3 px-4">Parking</th>
                                <th className="py-3 px-4">Pass</th>
                                <th className="py-3 px-4">Approval</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((v) => {
                                const status = v.status || (v.is_active === false ? "inactive" : "active");
                                const approvalStatus = v.approval_status || "approved";
                                return (
                                    <tr key={v.id} data-testid={`vehicle-mgmt-row-${v.id}`}
                                        onClick={() => setProfileVehicle(v)}
                                        className="border-b border-brand-line/60 last:border-0 cursor-pointer hover:bg-brand-bg/50">
                                        <td className="py-3 px-4 font-mono font-medium text-brand-ink">{v.registration_number}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{VEHICLE_TYPE_MAP[v.vehicle_type]?.label || v.vehicle_type}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{v.resident_name || "—"}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{v.flat_label || "—"}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{v.parking_slot_number || "Not assigned"}</td>
                                        <td className="py-3 px-4 font-mono text-xs text-brand-inkSoft">{v.parking_pass_number || "—"}</td>
                                        <td className="py-3 px-4" data-testid={`vehicle-mgmt-approval-${v.id}`}>
                                            <Chip variant={APPROVAL_STATUS_VARIANTS[approvalStatus] || "default"}>{APPROVAL_STATUS_LABELS[approvalStatus] || approvalStatus}</Chip>
                                        </td>
                                        <td className="py-3 px-4">
                                            <Chip variant={VEHICLE_STATUS_VARIANTS[status] || "default"}>{VEHICLE_STATUS_LABELS[status] || status}</Chip>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {approvalStatus === "pending" && (
                                                    <>
                                                        <Button size="sm" data-testid={`vehicle-mgmt-approve-btn-${v.id}`} disabled={approving}
                                                            onClick={(e) => { e.stopPropagation(); approveVehicle(v); }}
                                                            className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white text-xs h-7">
                                                            <CheckCircle size={13} className="mr-1" /> Approve
                                                        </Button>
                                                        <Button size="sm" variant="outline" data-testid={`vehicle-mgmt-reject-btn-${v.id}`} disabled={approving}
                                                            onClick={(e) => { e.stopPropagation(); openReject(v); }}
                                                            className="rounded-full border-brand-line text-xs h-7">
                                                            <XCircle size={13} className="mr-1" /> Reject
                                                        </Button>
                                                    </>
                                                )}
                                                <Button size="sm" variant="outline" data-testid={`vehicle-mgmt-edit-btn-${v.id}`}
                                                    onClick={(e) => { e.stopPropagation(); openEdit(v); }} className="rounded-full border-brand-line text-xs h-7">Edit</Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <VehicleFormDialog open={formOpen} onOpenChange={setFormOpen} flats={flats} editing={editingVehicle} onSaved={loadVehicles} />
            <VehicleProfileDialog open={!!profileVehicle} onOpenChange={(o) => !o && setProfileVehicle(null)} vehicle={profileVehicle}
                onApprove={approveVehicle} onReject={openReject} approving={approving} />

            <Dialog open={!!rejectVehicle} onOpenChange={(o) => !o && setRejectVehicle(null)}>
                <DialogContent className="rounded-sm max-w-md" data-testid="vehicle-reject-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl tracking-tight">Reject vehicle</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submitReject} className="space-y-4">
                        <p className="text-sm text-brand-inkSoft">
                            Rejecting <span className="font-mono font-medium text-brand-ink">{rejectVehicle?.registration_number}</span> — the resident will see this reason on My Flat.
                        </p>
                        <div className="space-y-2">
                            <Label>Reason for rejection</Label>
                            <Textarea data-testid="vehicle-reject-reason-input" required rows={3} value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Registration document unclear, please re-upload" className="rounded-sm border-brand-line" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setRejectVehicle(null)} className="rounded-full" data-testid="vehicle-reject-cancel-btn">Cancel</Button>
                            <Button type="submit" disabled={approving} data-testid="vehicle-reject-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {approving ? "Rejecting…" : "Reject vehicle"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
